import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { api, CUSTOMER_TOKEN, STAFF_TOKEN, areaForPath } from "@/lib/api";

const AuthCtx = createContext(null);

export const roleHome = { admin: "/admin", teller: "/teller", cashier: "/cashier", station_master: "/station", manager: "/management", bus_owner: "/account", customer: "/account" };
const HOME_ORDER = ["admin", "station_master", "manager", "teller", "cashier", "bus_owner", "customer"];

export const userRoles = (u) => {
  if (!u) return [];
  if (typeof u === "string") return [u];
  return u.roles?.length ? u.roles : (u.role ? [u.role] : []);
};
export const primaryRole = (u) => { const rs = userRoles(u); return HOME_ORDER.find((x) => rs.includes(x)) || rs[0] || null; };
export const homeFor = (u) => roleHome[primaryRole(u)] || "/account";
export const isStaffUser = (u) => userRoles(u).some((r) => r !== "customer");

export function AuthProvider({ children }) {
  const [customer, setCustomer] = useState(null); // null = checking, false = guest
  const [staff, setStaff] = useState(null);

  const refreshCustomer = useCallback(async () => {
    if (!localStorage.getItem(CUSTOMER_TOKEN)) return setCustomer(false);
    try { const { data } = await api.get("/auth/me", { area: "customer" }); setCustomer(data); }
    catch { localStorage.removeItem(CUSTOMER_TOKEN); setCustomer(false); }
  }, []);
  const refreshStaff = useCallback(async () => {
    if (!localStorage.getItem(STAFF_TOKEN)) return setStaff(false);
    try { const { data } = await api.get("/auth/me", { area: "staff" }); setStaff(data); }
    catch { localStorage.removeItem(STAFF_TOKEN); setStaff(false); }
  }, []);

  useEffect(() => { refreshCustomer(); refreshStaff(); }, [refreshCustomer, refreshStaff]);

  const value = {
    customer, staff,
    signInCustomer: (token, u) => { localStorage.setItem(CUSTOMER_TOKEN, token); setCustomer(u); },
    signInStaff: (token, u) => { localStorage.setItem(STAFF_TOKEN, token); setStaff(u); },
    signOutCustomer: () => { localStorage.removeItem(CUSTOMER_TOKEN); setCustomer(false); },
    signOutStaff: () => { localStorage.removeItem(STAFF_TOKEN); setStaff(false); },
    refreshCustomer, refreshStaff,
  };
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

// Area-aware: resolves `user`/`signIn`/`signOut`/`refresh` to the session that matches the
// page being viewed, so a logged-in staff member is never treated as a customer and vice versa.
export const useAuth = () => {
  const ctx = useContext(AuthCtx);
  const { pathname } = useLocation();
  const area = areaForPath(pathname);
  const user = area === "staff" ? ctx.staff : ctx.customer;
  const signIn = area === "staff" ? ctx.signInStaff : ctx.signInCustomer;
  const signOut = area === "staff" ? ctx.signOutStaff : ctx.signOutCustomer;
  const refresh = area === "staff" ? ctx.refreshStaff : ctx.refreshCustomer;
  return { ...ctx, area, user, signIn, signOut, refresh };
};
